import { type CSSProperties, type FC, memo, type ReactNode, useMemo } from "react";
import { cx } from "@stubhub/broadway-foundation/css";
import { Text } from "@stubhub/broadway-web";
import type { IconProps, PartnerIconProps } from "@stubhub/broadway-web/icons/types";
import { Content } from "src/shared/components/content";
import type { ContentId } from "src/shared/components/content/types";
import { performerLabelRecipe } from "./styles";

const ICON_SIZE = 16;
const PARTNER_ICON_HEIGHT = 12;

export interface PartnerIconDimensions extends CSSProperties {
  width: number;
  height: number;
}

export enum PerformerLabelIconType {
  STANDARD,
  PARTNER,
}

export type IconConfig =
  | { icon: FC<IconProps>; type: PerformerLabelIconType.STANDARD }
  | { icon: FC<PartnerIconProps>; type: PerformerLabelIconType.PARTNER };

interface SharedProps {
  startIcon?: IconConfig;
  endIcon?: IconConfig;
  background?: Exclude<CSSProperties["background"], number>;
  color?: CSSProperties["color"];
  iconColor?: CSSProperties["color"];
  partnerIconDimensions?: PartnerIconDimensions | undefined;
  textStyle?: Pick<CSSProperties, "fontSize" | "fontStyle" | "fontWeight" | "lineHeight"> | undefined;
  className?: string | undefined;
}

type Props = SharedProps & ({ contentId: ContentId; content?: never } | { content: ReactNode; contentId?: never });

/**
 * Component to display a label on performer pages, such as "MLB Authorized Ticket Marketplace", "official partner", or "Award winning artist"
 */
export const PerformerLabel: FC<Props> = memo(
  ({
    startIcon,
    contentId,
    content,
    endIcon,
    background,
    color,
    iconColor,
    partnerIconDimensions,
    textStyle,
    className = "",
  }) => {
    const styleVars = useMemo(
      () => ({
        "--component-accolade-background": background,
        "--component-accolade-color": color,
        "--component-accolade-icon-color": iconColor,
        "--component-accolade-font-size": textStyle?.fontSize,
        "--component-accolade-font-style": textStyle?.fontStyle,
        "--component-accolade-font-weight": textStyle?.fontWeight,
        "--component-accolade-line-height": textStyle?.lineHeight,
      }),
      [background, color, iconColor, textStyle],
    );

    const textAlign = !!startIcon && !endIcon ? "start" : !startIcon && !!endIcon ? "end" : undefined;

    const classes = performerLabelRecipe({
      textAlign,
    });

    return (
      <div className={cx(classes.root, !!className && className)} style={styleVars}>
        {startIcon && (
          <div className={classes.icon} style={styleVars}>
            {startIcon.type === PerformerLabelIconType.PARTNER ? (
              <startIcon.icon height={PARTNER_ICON_HEIGHT} color={"inherit"} style={partnerIconDimensions} />
            ) : (
              <startIcon.icon size={ICON_SIZE} color={"inherit"} />
            )}
          </div>
        )}
        <Text className={classes.text ?? ""} variant="label" size="small">
          {content ?? <Content id={contentId!} />}
        </Text>
        {endIcon && (
          <div className={classes.icon} style={styleVars}>
            {endIcon.type === PerformerLabelIconType.PARTNER ? (
              <endIcon.icon height={PARTNER_ICON_HEIGHT} color={"inherit"} style={partnerIconDimensions} />
            ) : (
              <endIcon.icon size={ICON_SIZE} color={"inherit"} />
            )}
          </div>
        )}
      </div>
    );
  },
);
PerformerLabel.displayName = "PerformerLabel";
